/**
 * Public Shared Customer List View
 * Read-only view for publicly shared customer lists
 */

const PublicSharedCustomers = () => {
  const [state, setState] = React.useState('loading'); // loading, loaded, error
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [showRegisterModal, setShowRegisterModal] = React.useState(false);
  const [hoveredRow, setHoveredRow] = React.useState(null);
  const [expiredCompany, setExpiredCompany] = React.useState(null);
  const [selectedCustomer, setSelectedCustomer] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('customer');

  const shareToken = window.SHARE_TOKEN;

  React.useEffect(() => {
    loadData();
  }, []);

  const customers = data ? data.customers : [];
  const filteredCustomers = React.useMemo(
    () => window.filterByRelationshipType ? window.filterByRelationshipType(customers, activeTab) : customers,
    [customers, activeTab]
  );

  const loadData = async () => {
    try {
      const res = await fetch(`/shared/list/${shareToken}/data`);
      const result = await res.json();

      if (result.success) {
        setData(result);
        setState('loaded');
      } else if ((result.error === 'expired' || result.error === 'no_longer_shared') && result.company) {
        setExpiredCompany(result.company);
        setError(result.error);
        setState('unavailable');
      } else {
        setError(result.error || 'Failed to load data');
        setState('unavailable');
      }
    } catch (err) {
      console.error('Error loading shared list:', err);
      setError('Failed to load data. Please try again.');
      setState('error');
    }
  };

  const handleExportClick = () => {
    setShowRegisterModal(true);
  };

  const handleWebsiteClick = (e, website) => {
    e.stopPropagation();
    const url = website.startsWith('http://') || website.startsWith('https://')
      ? website
      : `https://${website}`;
    window.open(url, '_blank');
  };

  const handleRowClick = (customer) => {
    setSelectedCustomer(customer);
  };

  if (state === 'loading') {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-16">
          <i className="ph-bold ph-spinner animate-spin text-3xl text-purple-600"></i>
          <p className="text-zinc-500 mt-3">Loading shared list...</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <i className="ph ph-warning-circle text-3xl text-red-500"></i>
          </div>
          <h2 className="text-xl font-bold text-zinc-900 mb-2">Unable to Load</h2>
          <p className="text-zinc-600 mb-6">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (state === 'unavailable') {
    const isExpired = error === 'expired';
    const isNoLongerShared = error === 'no_longer_shared';

    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <i className={`ph ${isExpired ? 'ph-clock-countdown' : 'ph-link-break'} text-4xl text-amber-600`}></i>
          </div>
          <h2 className="text-2xl font-bold text-zinc-900 mb-4">
            {isExpired ? 'This Link Has Expired' : 'This Link Is No Longer Available'}
          </h2>
          <p className="text-zinc-600 mb-8 text-lg">
            {isExpired
              ? 'The shared link you\'re trying to access has expired.'
              : 'The shared link you\'re trying to access is no longer available.'}
            {' '}However, you're entitled to
            <span className="font-semibold text-zinc-900"> 1 free customer list </span>
            when you register for NinjaPear.
          </p>

          {expiredCompany && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <img
                  src={`https://www.google.com/s2/favicons?domain=${expiredCompany.domain}&sz=64`}
                  alt=""
                  className="w-10 h-10 rounded-lg"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="text-lg font-medium text-zinc-900">{expiredCompany.domain}</span>
              </div>
              <p className="text-zinc-500 text-sm">
                Sign up to view customers of this company for free
              </p>
            </div>
          )}

          <a
            href="/auth/register"
            className="inline-flex items-center px-8 py-4 bg-purple-600 text-white rounded-xl font-semibold text-lg hover:bg-purple-700 transition-colors"
          >
            <i className="ph ph-user-plus mr-2"></i>
            Create Free Account
          </a>
          <p className="mt-4 text-sm text-zinc-500">
            Already have an account? <a href="/auth/login" className="text-purple-600 hover:underline">Sign in</a>
          </p>
        </div>
      </div>
    );
  }

  const { company, customers: allCustomers, total, shared_by } = data;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-zinc-500 mb-2">
          <i className="ph ph-share-network"></i>
          <span>Shared by {shared_by}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src={`https://www.google.com/s2/favicons?domain=${company.domain}&sz=128`}
              alt={company.name}
              className="w-12 h-12 rounded-xl"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">{company.name}</h1>
              <p className="text-zinc-500">{company.domain} - {total} companies</p>
            </div>
          </div>

          <button
            onClick={handleExportClick}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            <i className="ph ph-file-csv"></i>
            Export CSV
          </button>
        </div>
      </div>

      {/* Relationship Category Tabs */}
      {window.RelationshipTabs && (
        <RelationshipTabs
          customers={allCustomers}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      )}

      {/* Customer Table */}
      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-56" />
              <col className="w-44 hidden sm:table-column" />
              <col className="hidden md:table-column" />
              <col className="w-32 hidden sm:table-column" />
            </colgroup>
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-4 sm:px-6 py-4 text-sm font-semibold text-zinc-900">Company</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900 hidden sm:table-cell">Website</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900 hidden md:table-cell">Description</th>
                <th className="px-6 py-4 hidden sm:table-cell"></th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer, idx) => (
                <tr
                  key={customer.id || idx}
                  className={`border-b border-zinc-100 hover:bg-purple-50/50 transition-colors cursor-pointer
                             ${idx === filteredCustomers.length - 1 ? 'border-b-0' : ''}`}
                  onMouseEnter={() => setHoveredRow(customer.id || idx)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => handleRowClick(customer)}
                >
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${customer.website}&sz=64`}
                        alt=""
                        className="w-10 h-10 rounded-xl mr-3 shrink-0"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="min-w-0">
                        <span className="font-medium text-zinc-900 block truncate max-w-48" title={customer.name}>
                          {customer.name}
                        </span>
                        {customer.tagline && (
                          <div className="text-xs text-zinc-500 mt-0.5 truncate max-w-48" title={customer.tagline}>
                            {customer.tagline}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                    {customer.website ? (
                      <button
                        onClick={(e) => handleWebsiteClick(e, customer.website)}
                        className="flex items-center text-purple-600 hover:text-purple-800 hover:underline max-w-40"
                      >
                        <span className="truncate" title={customer.website}>{customer.website}</span>
                        <i className="ph ph-arrow-square-out ml-1.5 text-xs shrink-0"></i>
                      </button>
                    ) : (
                      <span className="text-zinc-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell">
                    <span className="text-zinc-600 text-sm truncate block" title={customer.description}>
                      {customer.description}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap hidden sm:table-cell">
                    <span className={`text-purple-600 text-sm font-medium whitespace-nowrap inline-flex items-center transition-opacity duration-150 ${hoveredRow === (customer.id || idx) ? 'opacity-100' : 'opacity-0'}`}>
                      View Customers <i className="ph ph-arrow-right ml-1"></i>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50">
          <div className="text-sm text-zinc-600">
            Showing {filteredCustomers.length} companies
          </div>
        </div>
      </div>

      {/* NinjaPear Promo Banner */}
      <div className="mt-8 bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Find More Customers Like These</h2>
            <p className="text-purple-100 max-w-xl">
              NinjaPear helps you discover companies that use your competitors' products.
              Get unlimited customer lists, export to CSV, and grow your sales pipeline.
            </p>
          </div>
          <a
            href="/auth/register"
            className="shrink-0 px-6 py-3 bg-white text-purple-700 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
          >
            Start Free Trial
          </a>
        </div>
      </div>

      {/* Register Modal */}
      {showRegisterModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowRegisterModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mx-auto mb-4">
                <i className="ph ph-download-simple text-2xl text-purple-600"></i>
              </div>

              <h2 className="text-xl font-bold text-zinc-900 text-center mb-2">
                Register to Download
              </h2>

              <p className="text-zinc-600 text-center mb-6">
                Create a free NinjaPear account to download this customer list as CSV and discover more potential customers for your business.
              </p>

              <div className="space-y-3">
                <a
                  href="/auth/register"
                  className="block w-full px-4 py-3 bg-purple-600 text-white rounded-lg text-center hover:bg-purple-700 transition-colors font-medium"
                >
                  <i className="ph ph-user-plus mr-2"></i>
                  Create Free Account
                </a>
                <a
                  href="/auth/login"
                  className="block w-full px-4 py-3 border border-zinc-200 text-zinc-700 rounded-lg text-center hover:bg-zinc-50 transition-colors font-medium"
                >
                  Already have an account? Sign in
                </a>
              </div>

              <button
                onClick={() => setShowRegisterModal(false)}
                className="mt-4 w-full text-sm text-zinc-500 hover:text-zinc-700"
              >
                Maybe later
              </button>
            </div>
          </div>
        </>
      )}

      {/* View Customer Modal */}
      {selectedCustomer && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setSelectedCustomer(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-center gap-3 mb-4">
                <img
                  src={`https://www.google.com/s2/favicons?domain=${selectedCustomer.website}&sz=64`}
                  alt=""
                  className="w-12 h-12 rounded-xl"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="text-left">
                  <h3 className="font-semibold text-zinc-900">{selectedCustomer.name}</h3>
                  <p className="text-sm text-zinc-500">{selectedCustomer.website}</p>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                    <i className="ph ph-gift text-xl text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-zinc-900 mb-1">1 Free Customer List</h4>
                    <p className="text-sm text-zinc-600">
                      Register for a free NinjaPear account and get your first customer list viewing completely free.
                      Discover who uses <span className="font-medium">{selectedCustomer.name}</span> and find your next customers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <a
                  href="/auth/register"
                  className="block w-full px-4 py-3 bg-purple-600 text-white rounded-lg text-center hover:bg-purple-700 transition-colors font-medium"
                >
                  <i className="ph ph-user-plus mr-2"></i>
                  Create Free Account
                </a>
                <a
                  href="/auth/login"
                  className="block w-full px-4 py-3 border border-zinc-200 text-zinc-700 rounded-lg text-center hover:bg-zinc-50 transition-colors font-medium"
                >
                  Already have an account? Sign in
                </a>
              </div>

              <button
                onClick={() => setSelectedCustomer(null)}
                className="mt-4 w-full text-sm text-zinc-500 hover:text-zinc-700"
              >
                Maybe later
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Mount the app
const container = document.getElementById('public-shared-customers');
if (container) {
  const root = ReactDOM.createRoot(container);
  root.render(<PublicSharedCustomers />);
}
